import { SALUTATIONS } from '../../../../lib/salutations';
import {
  PPA_EXPENSE_CLASS_LABEL,
  PPA_EXPENSE_CLASS_ORDER,
} from '../../../../lib/expenseClass';
import { addDays, todayCalendarDate } from '../../../../components/calendar/monthGrid';

/**
 * Sample values for the issuance form.
 *
 * <b>It fills every REQUIRED field, which is the Autogenerate contract: the primary action
 * enables on the first try.</b> It shipped filling only the free text on 20 Sep 2026, on the
 * reasoning that a generator cannot pick a line honestly — and that was the rule read backwards.
 * What the rule actually says is to <i>resolve foreign keys against the live catalog</i>: pick a
 * real row from the options the form already loaded. The item picker is fed by
 * `issuable-items`, which only ever offers what is genuinely in stock under the chosen method,
 * so picking from it is the honest path rather than an invented one.
 *
 * <b>The METHOD is still left alone</b>, because it reveals and hides a block of the form and
 * re-feeds the item picker — a generator that moved it would produce a different form on every
 * click, which is the rule that keeps a step-adding flag at its default.
 *
 * <b>And a line the reader has already chosen is left alone</b>, including one seeded from the
 * row the form was opened on. "Never a field the reader has already decided" names a seeded
 * value explicitly.
 *
 * <b>The external PARTY is an ORGANIZATION, never a person</b> — that is the column's own
 * contract — so its name is plausible vocabulary, like `sampleEducation`. Public offices
 * rather than named businesses. The contact number is from the reserved `+63 917 555` fiction
 * block regardless, because a number that could dial a real desk is not sample data.
 *
 * <b>The CONTACT PERSON is obviously synthetic, and that rule binds from 28 Sep 2026.</b> This
 * comment used to say nothing here reaches the identity store, which was true until a lend
 * could name an individual at the borrowing organization. That name is written to `rsys_pii`,
 * the one store whose whole purpose is holding real people — so a plausible fabrication there
 * is indistinguishable from a real one to whoever audits it later. Same pools as
 * `samplePersonnel`, for the same reason.
 */
const PURPOSES = [
  'Replenishment for the ward supply cabinet',
  'Issued against the approved requisition',
  'Field assignment for the quarter',
  'Replacement for a unit returned damaged',
  'Support for the community outreach activity',
];

const NOTES = [
  'Handed over at the receiving counter.',
  'Recipient acknowledged receipt on the slip.',
  'Counted and checked against the requisition.',
];

/** Public offices rather than named businesses — a lend goes to a body, not to a person. */
const PARTIES = [
  'Barangay Health Station',
  'Municipal Disaster Office',
  'Provincial Training Center',
  'District Evacuation Center',
];

/*
  OBVIOUSLY SYNTHETIC, because these land in `rsys_pii`.

  The same two pools `samplePersonnel` draws from: "is this row a person?" is the question
  that store exists to make answerable, and a generated Juan Dela Cruz sitting in it answers
  it wrongly to anybody reading the table later.
*/
const CONTACT_GIVEN = ['Sample', 'Probe', 'Fixture', 'Placeholder', 'Draft', 'Example'];
const CONTACT_FAMILY = ['Testcase', 'Testrow', 'Testentry', 'Testrecord'];

/*
  WHAT THE FORM CAN ACTUALLY SHOW — the three PPA expense classes, from the one map the
  picker draws.

  It was a pool of genuine fund clusters (General / Trust / Special Education), and those
  stopped being offerable on 06 Oct 2026 when the field became a closed dropdown. A generated
  value its own picker does not hold is the worst shape available: `<Select>` resolves its
  label from its options, so the field would read as UNANSWERED while still carrying the
  value — which is the one thing Autogenerate exists to prevent.

  THE LGU POOL IS GONE with the field it filled: the office is derived from the accountable
  officer own unit now, and the request has no slot for one.
*/
const FUND_CLUSTERS = PPA_EXPENSE_CLASS_ORDER.map((value) => PPA_EXPENSE_CLASS_LABEL[value]);

/** A street line, in the one box the form gives it. */
function streetLine(): string {
  return `${100 + Math.floor(Math.random() * 800)} Placeholder Road`;
}

/** Six characters is enough to keep a repeated click from colliding. */
function suffix(): string {
  return Math.random().toString(36).slice(2, 8).toUpperCase();
}

export function pick<T>(values: readonly T[]): T {
  return values[Math.floor(Math.random() * values.length)];
}

export function sampleIssuance(): {
  purpose: string;
  notes: string;
  externalParty: string;
  externalContact: string;

  /** Obviously synthetic — it is written to the identity store. */
  contactSalutation: string;
  contactFirstName: string;
  contactLastName: string;

  streetAddress: string;

  /** The ICS / PAR printable-form header. */
  fundCluster: string;

  /** Thirty days out. A lend past its date reads as overdue, so a generated one is not. */
  dueDate: string;
} {
  return {
    purpose: pick(PURPOSES),
    notes: pick(NOTES),
    externalParty: `${pick(PARTIES)} ${suffix()}`,
    externalContact: `+63 917 555 ${Math.floor(1000 + Math.random() * 9000)}`,
    contactSalutation: SALUTATIONS[Math.floor(Math.random() * SALUTATIONS.length)],
    contactFirstName: pick(CONTACT_GIVEN),
    contactLastName: pick(CONTACT_FAMILY),
    streetAddress: streetLine(),
    /*
      NO LGU, because the FORM no longer asks for one — it is derived from the accountable
      officer own unit, and a generator that filled a field the reader cannot see would be
      sending a value the request has no slot for.
    */
    fundCluster: pick(FUND_CLUSTERS),
    dueDate: addDays(todayCalendarDate(), 30),
  };
}
